import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote } from '../components/ui.jsx';

/**
 * Req #8: create and edit recipes.
 *
 * Ingredients are edited as free text, one per line, because that is how
 * recipes are written and read. The server parses each line into quantity,
 * unit, and item; the parse is shown back so a bad one can be spotted and
 * reworded rather than silently shopping for the wrong thing.
 */
export default function RecipeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const existing = useApi(`/recipes/${id}`, { skip: !editing });
  const { busy, error, run } = useAction();

  const [form, setForm] = useState({
    title: '',
    description: '',
    servings: '',
    prep_minutes: '',
    cook_minutes: '',
    total_minutes: '',
    tags: '',
    instructions: '',
    notes: '',
    source_url: '',
  });
  const [ingredientText, setIngredientText] = useState('');

  useEffect(() => {
    if (!existing.data) return;
    const data = existing.data;
    setForm({
      title: data.title || '',
      description: data.description || '',
      servings: data.servings ?? '',
      prep_minutes: data.prep_minutes ?? '',
      cook_minutes: data.cook_minutes ?? '',
      total_minutes: data.total_minutes ?? '',
      tags: (data.tags || []).join(', '),
      instructions: data.instructions || '',
      notes: data.notes || '',
      source_url: data.source_url || '',
    });
    // Rebuild the "For the sauce:" heading lines the parser consumed, so
    // editing and saving round-trips the grouping instead of flattening it.
    const lines = [];
    let group = null;
    for (const ingredient of data.ingredients) {
      if (ingredient.group_name && ingredient.group_name !== group) {
        if (lines.length) lines.push('');
        lines.push(`${ingredient.group_name}:`);
      }
      group = ingredient.group_name;
      lines.push(ingredient.raw_text);
    }
    setIngredientText(lines.join('\n'));
  }, [existing.data]);

  if (editing && existing.loading && !existing.data) return <Loading rows={6} />;

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const numeric = (value) => (value === '' || value === null ? null : Number(value));

  const save = () => run(async () => {
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      servings: numeric(form.servings),
      prep_minutes: numeric(form.prep_minutes),
      cook_minutes: numeric(form.cook_minutes),
      total_minutes: numeric(form.total_minutes),
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      instructions: form.instructions.trim() || null,
      notes: form.notes.trim() || null,
      source_url: form.source_url.trim() || null,
      ingredient_lines: ingredientText.split('\n').map((line) => line.trim()).filter(Boolean),
    };
    const recipe = editing
      ? await api.patch(`/recipes/${id}`, payload)
      : await api.post('/recipes', payload);
    navigate(`/recipes/${recipe.id}`);
  });

  const lineCount = ingredientText.split('\n').filter((l) => l.trim()).length;

  return (
    <div className="stack">
      <ErrorNote error={error} />

      <label>
        <span className="label-text">Title</span>
        <input value={form.title} onChange={set('title')} placeholder="Chicken and rice" autoFocus={!editing} />
      </label>

      <label>
        <span className="label-text">Description</span>
        <input value={form.description} onChange={set('description')} placeholder="Optional" />
      </label>

      <div className="field-row">
        <label>
          <span className="label-text">Serves</span>
          <input type="number" inputMode="numeric" value={form.servings} onChange={set('servings')} />
        </label>
        <label>
          <span className="label-text">Prep (min)</span>
          <input type="number" inputMode="numeric" value={form.prep_minutes} onChange={set('prep_minutes')} />
        </label>
        <label>
          <span className="label-text">Cook (min)</span>
          <input type="number" inputMode="numeric" value={form.cook_minutes} onChange={set('cook_minutes')} />
        </label>
      </div>

      <label>
        <span className="label-text">Ingredients — one per line</span>
        <textarea
          value={ingredientText}
          onChange={(event) => setIngredientText(event.target.value)}
          rows={10}
          placeholder={'2 cups all-purpose flour, sifted\n3 cloves garlic, minced\n1 (14.5 oz) can diced tomatoes\n\nFor the sauce:\n1/2 cup heavy cream'}
          spellCheck="false"
        />
      </label>
      <p className="tiny muted" style={{ marginTop: -6 }}>
        {lineCount} line{lineCount === 1 ? '' : 's'}. Write them the way the recipe does — quantities,
        units, and preparation are pulled apart automatically. A line ending in a colon becomes a heading.
      </p>

      <label>
        <span className="label-text">Method</span>
        <textarea
          value={form.instructions}
          onChange={set('instructions')}
          rows={8}
          placeholder={'1. Heat the oil.\n2. Brown the chicken on both sides.'}
        />
      </label>

      <label>
        <span className="label-text">Tags — comma separated</span>
        <input value={form.tags} onChange={set('tags')} placeholder="dinner, one-pot, freezer-friendly" />
      </label>

      <label>
        <span className="label-text">Notes</span>
        <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Optional" />
      </label>

      <label>
        <span className="label-text">Source link</span>
        <input type="url" inputMode="url" value={form.source_url} onChange={set('source_url')} placeholder="Optional" />
      </label>

      <div className="row">
        <button type="button" className="primary grow" disabled={busy || !form.title.trim()} onClick={save}>
          {busy ? <><span className="spinner" /> Saving</> : editing ? 'Save changes' : 'Create recipe'}
        </button>
        <button type="button" onClick={() => navigate(editing ? `/recipes/${id}` : '/recipes')}>Cancel</button>
      </div>
    </div>
  );
}
